/**
 * StyleNestedCards — the nested-cards list renderer.
 *
 * Aesthetic point-of-view:
 *   - Every node renders as a rounded card (radius-xl) with a generous
 *     4-pixel type-colored left accent rail — visible even at a glance
 *     across rooms.
 *   - Comfortable vertical rhythm: py-2.5 inside each card, mt-2 between
 *     sibling cards. The eye should never feel crowded.
 *   - Branch-key pill lives INSIDE the child card at the top-left, as
 *     an incoming-route chip. The connector between parent and child
 *     is a plain chevron tick so the card itself owns the DTMF context.
 *   - Hover: the card lifts (shadow-xs -> shadow-md) and the rail deepens.
 *   - A faint left-inset line groups a parent with its children, so the
 *     hierarchy reads as "family of cards", not just indentation.
 *
 * Reuses summariseNode + OnCompleteLabel + RecordingSlotChips from ../NodeRow
 * so the row body stays in lockstep with the canonical component.
 */
import { Fragment, forwardRef, useState } from 'react';
import type { IvrNodeRow } from '@/api/ivrNodes';
import type { IvrEdgeRow } from '@/api/ivrEdges';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import {
  ChevronRight, Plus, Pencil, X,
} from 'lucide-react';
import { isMenuLike } from '../AddChildPopover';
import {
  OnCompleteLabel,
  summariseNode,
  RecordingSlotChips,
  FolderLinkLabel,
  getRecordingFolderId,
  VoicemailBoxLabel,
  getVoicemailBoxId,
} from '../NodeRow';

// Mirror of the set in NodeRow.tsx — kept tiny + local instead of exported
// because it changes rarely and an extra cross-file dependency is not worth
// it. If you add a new folder-bound node type, update both lists.
const RECORDING_FOLDER_NODE_TYPES = new Set<string>([
  'series_folder',
  'sequential_folder',
  'unlock_series_folder',
  'unlock_sequential_folder',
  'bulletin',
  'latest_n_folder',
  'rotation_folder',
  'random_folder',
]);
const VOICEMAIL_NODE_TYPES = new Set<string>(['voicemail', 'listen_voicemail']);
import {
  accentFor, flavorForBranchKey, describeBranchKey, type StyleProps,
} from './common';

// ── Top-level component ──────────────────────────────────────────────────────

export function StyleNestedCards(props: StyleProps) {
  const {
    rootNodeId, nodeMap, adj, matchIds, expanded, flashNodeId,
    orphans, registerRowRef,
    onToggle, onEditNode, onDeleteNode, onAddChild, onJumpToNode,
    canMutate,
  } = props;

  return (
    <div className="px-4 py-4 bg-gradient-to-b from-slate-50/40 to-white">
      {rootNodeId && nodeMap[rootNodeId] ? (
        <CardSubtree
          nodeId={rootNodeId}
          parentEdge={null}
          depth={0}
          isLastChild
          visited={new Set()}
          nodeMap={nodeMap}
          adj={adj}
          expanded={expanded}
          rootNodeId={rootNodeId}
          matchIds={matchIds}
          canMutate={canMutate}
          flashNodeId={flashNodeId}
          ancestorInactive={false}
          onToggle={onToggle}
          onEditNode={onEditNode}
          onDeleteNode={onDeleteNode}
          onAddChild={onAddChild}
          onJumpToNode={onJumpToNode}
          registerRowRef={registerRowRef}
        />
      ) : (
        <div className="px-4 py-8 text-sm text-slate-400 italic text-center">
          No root node assigned — pick a root to see the tree view.
        </div>
      )}

      {orphans.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 flex items-center gap-2">
            <span className="inline-block h-px flex-1 bg-amber-200/70" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-600">
              Unreachable nodes — {orphans.length}
            </span>
            <span className="inline-block h-px flex-1 bg-amber-200/70" />
          </div>
          <div className="space-y-2">
            {orphans.map((node) => (
              <CardRow
                key={node.id}
                ref={(el) => registerRowRef(node.id, el)}
                node={node}
                edge={null}
                outgoingEdges={adj[node.id] ?? []}
                isRoot={false}
                isOrphan
                hasChildren={(adj[node.id] ?? []).length > 0}
                isExpanded={expanded.has(node.id)}
                canMutate={canMutate}
                onCompleteTarget={
                  node.on_complete === 'custom_node' && node.on_complete_node_id
                    ? nodeMap[node.on_complete_node_id] ?? null
                    : null
                }
                flashing={flashNodeId === node.id}
                onToggle={() => onToggle(node.id)}
                onEdit={() => onEditNode(node)}
                onDelete={onDeleteNode ? () => onDeleteNode(node) : undefined}
                onAddChild={(anchor) => onAddChild(node, anchor)}
                onJumpToNode={onJumpToNode}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Recursive subtree ────────────────────────────────────────────────────────

interface SubtreeProps {
  nodeId: string;
  parentEdge: IvrEdgeRow | null;
  depth: number;
  isLastChild: boolean;
  visited: Set<string>;
  nodeMap: Record<string, IvrNodeRow>;
  adj: { [from: string]: IvrEdgeRow[] };
  expanded: Set<string>;
  rootNodeId: string;
  matchIds: Set<string> | null;
  canMutate: boolean;
  flashNodeId: string | null;
  /**
   * True when ANY ancestor in this branch is inactive. Recursively
   * propagated from the root so a child renders a "parent inactive"
   * chip even if the immediate parent is active but a grandparent is
   * off. Sam (2026-05-19): operators need to know any node sitting
   * under an inactive ancestor is effectively unreachable.
   */
  ancestorInactive: boolean;
  onToggle: (id: string) => void;
  onEditNode: (n: IvrNodeRow) => void;
  onDeleteNode?: (n: IvrNodeRow) => void;
  onAddChild: (n: IvrNodeRow, anchor: DOMRect) => void;
  onJumpToNode: (id: string) => void;
  registerRowRef: (nodeId: string, el: HTMLDivElement | null) => void;
}

function CardSubtree(props: SubtreeProps) {
  const {
    nodeId, parentEdge, depth, visited, nodeMap, adj,
    expanded, rootNodeId, matchIds, canMutate, flashNodeId,
    ancestorInactive,
    onToggle, onEditNode, onDeleteNode, onAddChild, onJumpToNode,
    registerRowRef,
  } = props;

  const node = nodeMap[nodeId];
  if (!node) return null;

  // Cycle guard — show a compact card indicating the back-edge.
  if (visited.has(nodeId)) {
    return (
      <div
        className="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-1.5 text-[11px] italic text-amber-700"
        role="row"
      >
        <span aria-hidden>↩</span>
        back to {formatRef(node.ref_number)} ({node.name})
      </div>
    );
  }

  if (matchIds && !matchIds.has(nodeId)) return null;

  const childEdges = adj[nodeId] ?? [];
  const hasChildren = childEdges.length > 0;
  const isExpanded = expanded.has(nodeId);
  const childVisited = new Set(visited);
  childVisited.add(nodeId);
  const isRoot = nodeId === rootNodeId;
  const onCompleteTarget =
    node.on_complete === 'custom_node' && node.on_complete_node_id
      ? nodeMap[node.on_complete_node_id] ?? null
      : null;

  const visibleChildren = childEdges.filter(
    (e) => e.to_node_id && (!matchIds || matchIds.has(e.to_node_id)),
  );

  return (
    <div className={cn(depth === 0 ? '' : 'mt-2')}>
      <CardRow
        ref={(el) => registerRowRef(nodeId, el)}
        node={node}
        edge={parentEdge}
        outgoingEdges={childEdges}
        isRoot={isRoot}
        isOrphan={false}
        hasChildren={hasChildren}
        isExpanded={isExpanded}
        canMutate={canMutate}
        onCompleteTarget={onCompleteTarget}
        flashing={flashNodeId === nodeId}
        ancestorInactive={ancestorInactive}
        onToggle={() => onToggle(nodeId)}
        onEdit={() => onEditNode(node)}
        onDelete={onDeleteNode ? () => onDeleteNode(node) : undefined}
        onAddChild={(anchor) => onAddChild(node, anchor)}
        onJumpToNode={onJumpToNode}
      />

      {/* Children — nested inside a left-rail container so the family
          reads as one block. The rail is a thin gray line that sits
          inset from the parent's own rail, which makes depth visually
          distinguishable without relying purely on horizontal indent. */}
      {isExpanded && visibleChildren.length > 0 && (
        <div className="relative ms-6 mt-2 border-s border-dashed border-slate-200/80 ps-4 pb-0.5">
          {visibleChildren.map((edge, i) => (
            <Fragment key={edge.id}>
              <Connector isFirst={i === 0} />
              <CardSubtree
                nodeId={edge.to_node_id!}
                parentEdge={edge}
                depth={depth + 1}
                isLastChild={i === visibleChildren.length - 1}
                visited={childVisited}
                nodeMap={nodeMap}
                adj={adj}
                expanded={expanded}
                rootNodeId={rootNodeId}
                matchIds={matchIds}
                canMutate={canMutate}
                flashNodeId={flashNodeId}
                // Propagate inactive flag down — once any ancestor is
                // off, every descendant on this branch is unreachable.
                ancestorInactive={ancestorInactive || !node.active}
                onToggle={onToggle}
                onEditNode={onEditNode}
                onDeleteNode={onDeleteNode}
                onAddChild={onAddChild}
                onJumpToNode={onJumpToNode}
                registerRowRef={registerRowRef}
              />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Connector ────────────────────────────────────────────────────────────────
//
// A thin horizontal tick that reaches from the parent rail to the child
// card. No DTMF pill lives here any more — the branch-key chip is now
// rendered INSIDE the child card at the top-left as an incoming-route
// chip. A faint chevron keeps the elbow from reading as sparse.
function Connector({ isFirst }: { isFirst: boolean }) {
  return (
    <div className={cn('relative h-0 flex items-center', !isFirst && '')}>
      <span
        aria-hidden
        className="absolute -translate-y-1/2 -start-[calc(0.5rem+6px)] h-3 w-3 flex items-center justify-center rounded-full bg-white text-slate-300"
      >
        <ChevronRight className="h-2.5 w-2.5" />
      </span>
    </div>
  );
}

// ── The card row ─────────────────────────────────────────────────────────────

interface CardRowProps {
  node: IvrNodeRow;
  edge: IvrEdgeRow | null;
  outgoingEdges: ReadonlyArray<IvrEdgeRow>;
  isRoot: boolean;
  isOrphan: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  canMutate: boolean;
  onCompleteTarget: IvrNodeRow | null;
  flashing: boolean;
  /** True when any ancestor node on this branch is inactive — drives
   *  the "Parent inactive" chip on this card. False at the root. */
  ancestorInactive?: boolean;
  onToggle?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAddChild?: (anchor: DOMRect) => void;
  onJumpToNode?: (id: string) => void;
}

type SlotKey = 'main' | 'intro1' | 'intro2' | 'outro';

interface SlotDescriptor {
  key: SlotKey;
  label: string;      // e.g. "Main", "Intro 1"
  recordingId: string;
}

/** Collect the recording slots the card should advertise. Empty slots
 *  are omitted entirely so the row stays quiet when nothing is attached. */
function collectSlots(node: IvrNodeRow): SlotDescriptor[] {
  const out: SlotDescriptor[] = [];
  if (node.main_recording_id) {
    out.push({ key: 'main', label: 'Main', recordingId: node.main_recording_id });
  }
  if (node.intro1_recording_id) {
    out.push({ key: 'intro1', label: 'Intro 1', recordingId: node.intro1_recording_id });
  }
  if (node.intro2_recording_id) {
    out.push({ key: 'intro2', label: 'Intro 2', recordingId: node.intro2_recording_id });
  }
  if (node.outro_recording_id) {
    out.push({ key: 'outro', label: 'Outro', recordingId: node.outro_recording_id });
  }
  return out;
}

const CardRow = forwardRef<HTMLDivElement, CardRowProps>(function CardRow(
  {
    node, edge, outgoingEdges, isRoot, isOrphan, hasChildren, isExpanded,
    canMutate, onCompleteTarget, flashing, ancestorInactive = false,
    onToggle, onEdit, onDelete, onAddChild, onJumpToNode,
  },
  ref,
) {
  const accent = accentFor(node.node_type);
  const summary = summariseNode(node, outgoingEdges);
  const menuLike = isMenuLike(node.node_type);

  // Branch-key chip — shown INSIDE the card at the top-left when this
  // node is reached via an incoming edge. We always show it so the
  // inbound-route context travels with the card.
  const branchKey = edge?.branch_key ?? null;
  const branchFlavor = flavorForBranchKey(branchKey, false, false);

  return (
    <div
      ref={ref}
      role="treeitem"
      aria-level={undefined}
      aria-expanded={hasChildren ? isExpanded : undefined}
      className={cn(
        'group relative rounded-xl border bg-white overflow-hidden',
        'transition-all duration-150',
        'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
        'hover:shadow-[0_4px_14px_-4px_rgba(15,23,42,0.10)] hover:-translate-y-px',
        accent.border,
        !node.active && 'opacity-55',
        flashing && 'ring-2 ring-indigo-400/60',
        isRoot && 'ring-1 ring-indigo-200/70',
      )}
      onClick={() => onEdit?.()}
    >
      {/* Thick left accent rail */}
      <span
        aria-hidden
        className={cn('absolute inset-y-0 start-0 w-1.5', accent.solid)}
      />

      <div className="flex items-center gap-3 ps-5 pe-3 py-2.5 cursor-pointer">
        {/* Chevron */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle?.(); }}
          className={cn(
            'shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-slate-400',
            hasChildren
              ? 'hover:bg-slate-100 hover:text-slate-700'
              : 'invisible',
          )}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            className={cn(
              'h-4 w-4 transition-transform duration-150',
              isExpanded && 'rotate-90',
            )}
          />
        </button>

        {/* Branch-key chip (inside the card, at the top-left). Shown for
            any non-root node that was reached via an incoming edge. */}
        {branchKey && !isRoot && !isOrphan && (
          <span
            aria-label={`branch key ${branchKey}`}
            title={describeBranchKey(branchKey)}
            className={cn(
              'shrink-0 inline-flex items-center justify-center rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold leading-none min-w-[22px] border',
              branchFlavor === 'special' && 'bg-amber-50 text-amber-700 border-amber-200',
              branchFlavor === 'next' && 'bg-slate-50 text-slate-600 border-slate-200',
              branchFlavor === 'dtmf' && 'bg-indigo-600 text-white border-indigo-600',
            )}
          >
            {branchKey}
          </span>
        )}

        {isRoot && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-indigo-700">
            ROOT
          </span>
        )}
        {isOrphan && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-amber-700">
            orphan
          </span>
        )}
        {!node.active && (
          <span
            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-rose-700"
            title="This node is inactive — the engine skips it. Edit the node and flip Active on to use it."
          >
            INACTIVE
          </span>
        )}
        {node.active && ancestorInactive && (
          <span
            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[10px] font-medium text-rose-700"
            title="An ancestor node in this branch is inactive — this node is unreachable until that node is active again."
          >
            parent inactive
          </span>
        )}

        {/* Ref number + type */}
        <span className="shrink-0 font-mono text-[11px] text-slate-400">
          {formatRef(node.ref_number)}
        </span>
        <NodeTypeBadge type={node.node_type} />

        {/* Name */}
        {node.name && (
          <span className="truncate text-[13.5px] font-medium text-slate-900 min-w-0 flex-1">
            {node.name}
          </span>
        )}

        {/* Summary — folder + voicemail-box bound node types render the
            clickable label (with the resource name); other types fall back
            to the plain summariseNode text. */}
        {RECORDING_FOLDER_NODE_TYPES.has(node.node_type) ? (
          <FolderLinkLabel folderId={getRecordingFolderId(node)} />
        ) : VOICEMAIL_NODE_TYPES.has(node.node_type) ? (
          <VoicemailBoxLabel
            boxId={getVoicemailBoxId(node)}
            nodeType={node.node_type as 'voicemail' | 'listen_voicemail'}
          />
        ) : (
          summary && (
            <span className="hidden md:inline text-[11.5px] text-slate-500 truncate max-w-[180px]">
              {summary}
            </span>
          )
        )}

        {/* On-complete label for non-menu nodes */}
        {!menuLike && (
          <OnCompleteLabel
            node={node}
            target={onCompleteTarget}
            onJumpToNode={onJumpToNode}
          />
        )}

        {/* Edge label (if any) */}
        {edge?.label && (
          <span className="hidden xl:inline text-[11px] text-slate-400 italic truncate max-w-[110px]">
            &ldquo;{edge.label}&rdquo;
          </span>
        )}

        {/* Recording slot chips — all 4 slots, filled=clickable, empty=muted */}
        <RecordingSlotChips node={node} />

        {/* Actions — revealed on hover/focus */}
        <div className={cn(
          'ms-auto shrink-0 flex items-center gap-0.5',
          'opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity',
        )}>
          {canMutate && menuLike && onAddChild && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                onAddChild(rect);
              }}
              className="h-7 w-7 inline-flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700"
              aria-label={`Add child to ${node.name}`}
              title="Add child"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label={`Edit ${node.name}`}
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {canMutate && onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
              aria-label={`Delete ${node.name}`}
              title="Delete"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
